import { motion } from "framer-motion";
import { Rocket, Gamepad2 } from "lucide-react";

export const AppIcon = ({ icon, color = "#FFC72C", box = "w-16 h-16", text = "text-4xl" }) => (
  <div
    className={`${box} rounded-2xl border-2 border-ink flex items-center justify-center overflow-hidden shrink-0`}
    style={{ backgroundColor: color }}
  >
    {icon && icon.startsWith("http") ? (
      <img src={icon} alt="" className="w-full h-full object-cover" />
    ) : (
      <span className={text}>{icon || "🎮"}</span>
    )}
  </div>
);

export const AppCard = ({ app, testid }) => (
  <motion.article
    data-testid={testid || `app-card-${app.id}`}
    whileHover={{ x: -4, y: -4 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className="bg-white rounded-3xl border-2 border-ink shadow-brutal-sm hover:shadow-brutal p-6 sm:p-7 flex flex-col gap-4 h-full"
  >
    <div className="flex items-start justify-between gap-3">
      <AppIcon icon={app.icon} color={app.color} />
      <span className="text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-sun border-2 border-ink whitespace-nowrap">
        {app.age_range}
      </span>
    </div>
    <div>
      <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{app.name}</h3>
      {app.category && (
        <span className="inline-block mt-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-skykit/15 text-sky-700 border border-skykit/40">
          {app.category}
        </span>
      )}
    </div>
    <p className="text-sm sm:text-base text-ink/70 leading-relaxed">{app.description}</p>
    {app.screenshots && app.screenshots.length > 0 && (
      <div
        data-testid={`app-${app.id}-screenshots`}
        className="flex gap-3 overflow-x-auto pb-1 snap-x -mx-1 px-1 flex-1 items-start"
      >
        {app.screenshots.map((s, i) => (
          <img
            key={i}
            src={s}
            alt={`${app.name} preview ${i + 1}`}
            loading="lazy"
            className="h-32 w-auto rounded-xl border-2 border-ink object-cover snap-start shrink-0"
          />
        ))}
      </div>
    )}
    {(!app.screenshots || app.screenshots.length === 0) && <div className="flex-1" />}
    {app.tags && app.tags.length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        {app.tags.map((t) => (
          <span
            key={t}
            className="text-xs font-bold px-2.5 py-1 rounded-full bg-cream border border-ink/20 text-ink/70"
          >
            {t}
          </span>
        ))}
      </div>
    )}
    <div className="flex flex-wrap items-center gap-3">
      {app.play_url && (
        <a
          href={app.play_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`app-${app.id}-play-button`}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-coral text-white font-display font-extrabold border-2 border-ink shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal transition-all duration-200"
        >
          <Gamepad2 size={18} /> Play online now
        </a>
      )}
      <span
        data-testid={`app-${app.id}-coming-soon`}
        className="inline-flex items-center gap-2 self-start text-xs font-black uppercase tracking-widest px-3.5 py-2 rounded-full bg-grape/15 text-ink border-2 border-dashed border-ink/40"
      >
        <Rocket size={13} className="text-coral" /> Coming soon to app stores
      </span>
    </div>
  </motion.article>
);

export const MysteryCard = () => (
  <motion.article
    data-testid="mystery-game-card"
    whileHover={{ x: -4, y: -4 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className="rounded-3xl border-2 border-dashed border-ink/40 bg-white/50 p-6 sm:p-7 flex flex-col items-center justify-center text-center gap-4 h-full min-h-[300px]"
  >
    <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-ink/40 bg-cream flex items-center justify-center">
      <span className="font-display font-black text-3xl text-ink/40 select-none">?</span>
    </div>
    <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-ink/60">
      A new game is brewing
    </h3>
    <p className="text-sm text-ink/50 leading-relaxed max-w-[250px]">
      Something exciting is taking shape in the KITOE lab. Check back soon for the big
      reveal.
    </p>
    <span className="text-xs font-black uppercase tracking-widest px-3.5 py-2 rounded-full bg-sun/50 border-2 border-dashed border-ink/30 text-ink/60">
      Coming soon
    </span>
  </motion.article>
);
